import { toast } from "react-toastify";
import {
    initializeExportDispatcher,
    getDownloadFileLink,
} from "../../store/slices/exportFile/exportFile";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_TRIES = 40; // ~2 minutes

/**
 * [IVR_SYNC] Download an incremental-sync change file — the contacts a failed run
 * tried to ADD or REMOVE (each row carries its Job Id). Mirrors the contacted-
 * numbers export flow: initialize → poll → browser download; the link is also
 * emailed by the backend.
 *
 * @param {object} args
 * @param {function} args.dispatch        redux dispatch
 * @param {"IVR_SYNC_ADDED_EXPORT"|"IVR_SYNC_REMOVED_EXPORT"} args.exportType
 * @param {number|string} args.campaignId
 * @param {string} args.campaignUuid
 * @param {string} [args.runId]           optional; backend uses the latest run if omitted
 * @param {string} [args.fileLabel]       used for the downloaded file name
 */
export async function downloadIvrSyncChangeFile({
    dispatch,
    exportType,
    campaignId,
    campaignUuid,
    runId,
    fileLabel = "changes",
}) {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
        toast.error("User not found. Please re-login.");
        return;
    }
    if (!campaignId || !campaignUuid) {
        toast.error("Campaign details are missing.");
        return;
    }

    const payload = {
        exportType,
        campaignId,
        campaignUuid,
        runId,
        user: user?.data,
    };

    // 1) Kick off the async export (validates permission + enqueues the job).
    const initMsg = await dispatch(initializeExportDispatcher(payload)).unwrap();
    toast.info(
        initMsg || "Generating file… the download link will also be emailed to you."
    );

    // 2) Poll for the generated file. "Not ready" rejects the thunk → keep polling.
    for (let i = 0; i < MAX_POLL_TRIES; i++) {
        await sleep(POLL_INTERVAL_MS);
        try {
            const res = await dispatch(getDownloadFileLink(payload)).unwrap();

            if (res && res.status === "failed") {
                toast.error(res.message || "Export failed. Please try again.");
                return;
            }

            if (res && res.filePath) {
                const link = document.createElement("a");
                link.href = res.filePath;
                link.download = `${campaignUuid}_${fileLabel}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success(
                    "File downloaded. A copy of the link was also emailed to you."
                );
                return;
            }

            if (res && res.filePath === null) {
                toast.warn(`No ${fileLabel} found for this campaign run.`);
                return;
            }
        } catch (err) {
            // not ready yet — keep polling
        }
    }

    toast.error(
        "File is taking longer than expected. The download link will be emailed to you once ready."
    );
}
